export type TestModuleStatus = 'DRAFT' | 'READY' | 'ACTIVE' | 'DEPRECATED';

export const TEST_MODULE_STATUSES: readonly TestModuleStatus[] = ['DRAFT', 'READY', 'ACTIVE', 'DEPRECATED'];

export const TEST_MODULE_AUTOMATION_FRAMEWORKS = [
  'PLAYWRIGHT',
  'SELENIUM',
  'CYPRESS',
  'ROBOT_FRAMEWORK',
  'POSTMAN_NEWMAN',
] as const;
export type TestModuleAutomationFramework = (typeof TEST_MODULE_AUTOMATION_FRAMEWORKS)[number];

export interface TestModule {
  id: string;
  /** Menschenlesbarer, projektweit laufender Key (z. B. 'MOD-018', NEX-654). In ID-Pill statt UUID angezeigt. */
  key: string;
  projectId: string;
  name: string;
  description?: string | null;
  version: number;
  status: TestModuleStatus;
  authorUserId?: string | null;
  authorName?: string | null;
  tags?: string[];
  stepCount?: number;
  usageCount?: number;
  moduleUsageCount?: number;
  automationKey?: string | null;
  automationStatus?: import('./test-case.model').AutomationStatus;
  automationFramework?: TestModuleAutomationFramework | null;
  /** NEX-2257: ruft sich selbst auf oder liegt auf einem Kreis von Modulaufrufen. Fehlt = false. */
  recursive?: boolean;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Ebene, nach der ein Lauf mit rekursiven Modulaufrufen abbricht (NEX-2256, gleich der Grenze im Backend). */
export const MODULE_CALL_MAX_DEPTH = 32;

/** Ein in ein Modul eingebundenes anderes Modul (Junction-Eintrag, NEX-462). */
export interface EmbeddedModule {
  embedId: string;
  moduleId: string;
  /**
   * Menschenlesbarer Key des eingebundenen Moduls (MOD-###, NEX-654). Null,
   * wenn das referenzierte Modul nicht (mehr) auflösbar ist (analog name).
   */
  key: string | null;
  name: string;
  stepCount: number;
  orderIndex: number;
}

/** Datentyp eines Modul-Parameters (NEX-704). SECRET ist nur für Eingaben zulässig. */
export type TestModuleParameterType = 'TEXT' | 'SECRET' | 'NUMBER' | 'BOOLEAN' | 'DATE';

export const TEST_MODULE_PARAMETER_TYPES: readonly TestModuleParameterType[] = [
  'TEXT',
  'SECRET',
  'NUMBER',
  'BOOLEAN',
  'DATE',
];

/**
 * Deklarierte Modul-Schnittstelle (NEX-704): Eingabe-Parameter (INPUT) oder
 * Rückgabe (OUTPUT). Bei OUTPUT sind required/defaultValue bedeutungslos
 * (Server erzwingt required=false, defaultValue=null).
 */
export interface TestModuleParameter {
  id?: string | null;
  /** Lokale Draft-ID vor Persistenz; wird beim Save gegen die Server-UUID getauscht. */
  clientId?: string | null;
  name: string;
  type: TestModuleParameterType;
  required: boolean;
  defaultValue?: string | null;
}

/** Woher ein Modul-Probelauf seine Werte nimmt (NEX-2433, `GET …/run-parameters`). */
export interface ModuleRunParameterSource {
  kind: 'DATASHEET' | 'DEFAULTS';
  dataSheetId?: string;
  dataSheetKey?: string;
  dataSheetName?: string;
}

/** `value` fehlt bei einem Secret immer; ob ein Wert feststeht, sagt `hasValue`. */
export interface ModuleRunParameter {
  name: string;
  type: TestModuleParameterType;
  required: boolean;
  secret: boolean;
  value?: string;
  hasValue: boolean;
  missing: boolean;
}

export interface ModuleRunParameters {
  source: ModuleRunParameterSource;
  parameters: ModuleRunParameter[];
}

export interface TestModuleDataSheetColumnMapping {
  id?: string | null;
  testdatenblattId: string;
  sheetColumn: string;
  parameterName: string | null;
}

export interface TestModuleDetail extends TestModule {
  steps: import('./test-case.model').TestStep[];
  embeddedModules: EmbeddedModule[];
  inputs?: TestModuleParameter[];
  outputs?: TestModuleParameter[];
  columnMappings?: TestModuleDataSheetColumnMapping[];
}

export type TestModuleActivityType =
  | 'CREATED'
  | 'EDITED'
  | 'MODULE_EMBEDDED'
  | 'MODULE_UNEMBEDDED'
  | 'DATASHEET_LINKED'
  | 'DATASHEET_UNLINKED'
  | 'HANDED_OVER'
  | 'HANDOVER_CLOSED'
  | 'HANDOVER_REOPENED'
  | 'HANDOVER_SKIPPED';

export interface TestModuleActivity {
  id: string;
  type: TestModuleActivityType;
  actorUserId: string;
  actorName: string | null;
  detail: string | null;
  createdAt: string;
}

export type TestModuleUsageKind = 'TEST_CASE' | 'MODULE';

export interface TestModuleUsage {
  id: string;
  key: string;
  title: string;
  kind?: TestModuleUsageKind;
}

export interface CreateTestModuleRequest {
  name: string;
  description?: string | null;
  status?: TestModuleStatus;
  authorUserId?: string | null;
  tags?: string[];
}

export interface UpdateTestModuleRequest {
  name?: string;
  description?: string | null;
  status?: TestModuleStatus;
  authorUserId?: string | null;
  clearAuthorUserId?: boolean;
  tags?: string[];
}

export interface TestModuleStepDraft {
  id?: string | null;
  clientId?: string | null;
  /** UUID der zu verknuepfenden projektweiten TestStep-Definition (NEX-546); null = neuer Schritt. */
  testStepId?: string | null;
  kind: import('./test-case.model').TestStepKind;
  title?: string | null;
  action?: string | null;
  expectedResult?: string | null;
  notes?: string | null;
  tcdl?: string | null;
  status?: import('./test-case.model').TestStepRunStatus | null;
  calledModuleId?: string | null;
  stepRole?: import('./test-case.model').StepRole | null;
  category?: import('./test-case.model').StepCategory | null;
  assigneeUserId?: string | null;
  tags?: string[];
  /** NEX-1004: TDB-Override am MODULE_REF-Step innerhalb eines Moduls; null loescht die Zuordnung. */
  dataSheetId?: string | null;
  dataSheetFilters?: Record<string, string>[] | null;
  /** NEX-1053: Bedingung am MODULE_REF-Step; null entfernt sie. */
  stepCondition?: Record<string, string> | null;
  /** Ganze Bedingungsliste; gesetzt geht sie `stepCondition` vor. */
  stepConditions?: Record<string, string>[] | null;
}

export interface TestModuleEmbedDraft {
  id?: string | null;
  clientId?: string | null;
  embeddedModuleId: string;
}

/**
 * Buffered-Save-Request des Testmodul-Formulars (NEX-601): ein POST/PUT
 * ersetzt granulare Feld-, Step- und Embed-Requests. Analog zum Testfall
 * (SaveFullTestCaseRequest, NEX-599).
 */
export interface SaveFullTestModuleRequest {
  name: string;
  description?: string | null;
  status?: TestModuleStatus;
  authorUserId?: string | null;
  tags?: string[];
  notes?: string | null;
  steps: TestModuleStepDraft[];
  embeds: TestModuleEmbedDraft[];
  /** Eingabe-Parameter-Drafts (NEX-704); null = Bereich unverändert (kein Diff). */
  inputs?: TestModuleParameter[];
  /** Rückgabe-Drafts (NEX-704); null = Bereich unverändert (kein Diff). */
  outputs?: TestModuleParameter[];
  columnMappings?: TestModuleDataSheetColumnMapping[];
  automationStatus?: import('./test-case.model').AutomationStatus;
  automationFramework?: TestModuleAutomationFramework | null;
  expectedVersion?: number | null;
}

export interface SaveFullTestModuleResponse {
  module: TestModuleDetail;
  steps: import('./test-case.model').TestStep[];
  embeds: EmbeddedModule[];
  inputs?: TestModuleParameter[];
  outputs?: TestModuleParameter[];
  columnMappings?: TestModuleDataSheetColumnMapping[];
  /** Client-Draft-IDs → finale UUIDs (Steps, Embeds und Parameter gemeinsam). */
  clientIdMapping: Record<string, string>;
}
